import {
  SetStateAction,
  Dispatch,
  ChangeEvent,
  Fragment,
  useEffect,
  useState,
} from 'react';

import { MyApp } from './types/myAppTypes';
import { DocumentNode } from 'graphql';
import { useLazyQuery } from '@apollo/client';
import {
  DevHubBox,
  DevHubButton,
  DevHubCheckbox,
  DevHubCircularProgress,
  DevHubGrid,
  DevHubModal,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { CSVDownload } from 'react-csv';

type Props = {
  queryFilterState: Record<string, string>;
  GetApplicationsQuery: DocumentNode;
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsExporting: Dispatch<SetStateAction<boolean>>;
  setErrorOccurred: Dispatch<SetStateAction<boolean>>;
  totalAppCount: number;
};

const appDetailOptions = [
  'Application Id',
  'Name',
  'Application Group',
  'Service Domain',
  'Description',
  'Information Classification Policy',
  'Owner',
  'Privacy',
  'Roles',
  'Status',
];

interface MyAppExportOptions {
  'Application Id': string;
  'Application Group': string;
  'Service Domain': string;
  Description: string;
  'Information Classification Policy'?: string;
  Name: string;
  Owner?: string;
  Privacy?: string;
  Roles?: string;
  Status: string;
}

/**
 * Renders a component for exporting CSV data.
 * @param props - The component props.
 * @returns The rendered component.
 */
const CSVExport = (props: Props): JSX.Element => {
  const [applications, setApplications] = useState<MyApp[]>([]);
  const [checkedAttributes, setCheckedAttributes] = useState<string[]>([
    'Application Id',
    'Name',
  ]);
  const [isDownloadEnabled, setIsDownloadEnabled] = useState(false);
  const [isAllChecked, setIsAllChecked] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const [getApplications, { loading, error, data, fetchMore }] = useLazyQuery(
    props.GetApplicationsQuery,
    {
      variables: {
        filter: props.queryFilterState,
        pagination: {
          limit: 100,
          offset: 0,
        },
      },
    },
  );

  const fetchMoreApplications = async (paginationOffset: number) => {
    try {
      const res = await fetchMore({
        variables: {
          filter: props.queryFilterState,
          pagination: {
            limit: 100,
            offset: paginationOffset,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return fetchMoreResult;
        },
      });

      const total = res?.data?.resources?.applicationsPaged?.totalCount;
      setApplications((prev: MyApp[]) => [
        ...prev,
        ...res?.data?.resources?.applicationsPaged?.applications,
      ]);

      if (paginationOffset + 100 >= total) {
        setIsDownloadEnabled(true);
      }
    } catch (err) {
      props.setErrorOccurred(true);
    }
  };

  const handleExportClick = async () => {
    const variables = {
      filter: props.queryFilterState,
      pagination: {
        limit: 100,
        offset: 0,
      },
    };

    setIsLoading(true);
    try {
      props.setIsModalOpen(false);
      props.setIsExporting(true);

      const { data: initialData } = await getApplications({ variables });

      const totalCount = initialData?.resources?.applicationsPaged?.totalCount;

      for (let offset = 0; offset < totalCount; offset += 100) {
        fetchMoreApplications(offset);
      }
    } catch (err) {
      props.setErrorOccurred(true);
    } finally {
      setIsLoading(false);
      setIsAllChecked(false);
    }
  };

  const formatDataForCSV = (): MyAppExportOptions[] => {
    return applications?.map((app: MyApp) => {
      return {
        'Application Id': app.applicationId.toString(),
        'Application Group': app.appGroup,
        'Service Domain': app.serviceDomain,
        Description: app.description
          .replaceAll('"', '')
          .replaceAll('\\', '')
          .replace(/[\n\r]/g, ' '),
        'Information Classification Policy':
          app.informationClassificationPolicy?.name,
        Name: app.name,
        Owner: app.owner?.displayName,
        Roles: app.roles
          ?.map((role: { displayName: string }) => role.displayName)
          .join(','),
        Status: app.status,
      };
    });
  };
  const generateCSVData = () => {
    const dataToExport = formatDataForCSV() ?? [];

    const resultApplications = dataToExport.map(datum => {
      const filteredData: Partial<MyAppExportOptions> = {};
      checkedAttributes.forEach((attribute: string) => {
        filteredData[attribute as keyof MyAppExportOptions] =
          datum[attribute as keyof MyAppExportOptions];
      });
      return filteredData;
    });

    return resultApplications;
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;

    if (value === 'select-all') {
      setCheckedAttributes(checked ? [...appDetailOptions] : []);
      setIsAllChecked(checked);
    } else {
      const updatedCheckedAttributes = checked
        ? [...checkedAttributes, value]
        : checkedAttributes.filter(attribute => attribute !== value);

      setCheckedAttributes(updatedCheckedAttributes);
      setIsAllChecked(
        appDetailOptions.every(option =>
          updatedCheckedAttributes.includes(option),
        ),
      );
    }
  };

  const handleCloseModal = () => {
    props.setIsModalOpen(false);
    setCheckedAttributes(['Application Id', 'Name']);
    setIsAllChecked(false);
  };

  const disableChecked = (option: string) => {
    return option === 'Application Id' || option === 'Name';
  };

  useEffect(() => {
    if (props.isModalOpen) {
      setApplications([]);
      setCheckedAttributes(['Application Id', 'Name']);
    }
    if (error) {
      props.setErrorOccurred(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isModalOpen, error]);

  useEffect(() => {
    if (
      applications.length === data?.resources?.applicationsPaged?.totalCount
    ) {
      props.setIsExporting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications, data?.resources?.applicationsPaged?.totalCount]);

  return (
    <Fragment>
      <DevHubBox display="flex" justifyContent="center" mt={1}>
        <DevHubModal open={props.isModalOpen} onClose={handleCloseModal}>
          <DevHubGrid container>
            <DevHubTypography variant="h4" fontColor="primary">
              Export Application Data to CSV of {props.totalAppCount}{' '}
              Application{props.totalAppCount > 1 ? 's' : ''}
            </DevHubTypography>
            <DevHubGrid item xs={12}>
              <DevHubBox display="flex" justifyContent="space-between">
                <DevHubTypography variant="h5" fontColor="primary">
                  Select All
                </DevHubTypography>

                <DevHubCheckbox
                  key="select-all"
                  value="select-all"
                  name="select-all"
                  onChange={handleCheckboxChange}
                  checked={isAllChecked}
                  color="primary"
                />
              </DevHubBox>
            </DevHubGrid>

            {appDetailOptions?.map?.((option: string) => {
              return (
                <DevHubGrid item xs={12} key={option}>
                  <DevHubBox display="flex" justifyContent="space-between">
                    <DevHubTypography>{option}</DevHubTypography>
                    {disableChecked(option) ? (
                      <DevHubTooltip title={`You cannot unselect ${option}`}>
                        <span>
                          <DevHubCheckbox
                            key={option}
                            value={option}
                            name={option}
                            disabled
                            checked
                            color="secondary"
                          />
                        </span>
                      </DevHubTooltip>
                    ) : (
                      <DevHubCheckbox
                        key={option}
                        value={option}
                        name={option}
                        onChange={handleCheckboxChange}
                        disabled={disableChecked(option)}
                        checked={checkedAttributes?.includes(option)}
                        color="primary"
                      />
                    )}
                  </DevHubBox>
                </DevHubGrid>
              );
            })}
            <DevHubBox
              display="flex"
              justifyContent="flex-end"
              width="100%"
              m="1rem"
            >
              {!isLoading && !loading ? (
                <DevHubButton
                  variant="contained"
                  color="primary"
                  onClick={handleExportClick}
                >
                  Generate CSV
                </DevHubButton>
              ) : (
                <DevHubCircularProgress />
              )}
            </DevHubBox>
          </DevHubGrid>
        </DevHubModal>
      </DevHubBox>
      {isDownloadEnabled &&
        !loading &&
        !error &&
        applications.length ===
          data?.resources?.applicationsPaged?.totalCount && (
          <Fragment>
            <CSVDownload
              filename="AppList"
              data={generateCSVData()}
              target="_blank"
            />
          </Fragment>
        )}
    </Fragment>
  );
};

export default CSVExport;
